import React from 'react'
import type { WithStyles, Theme } from '@material-ui/core/styles'
import { createStyles, withStyles } from '@material-ui/core/styles'
import type { WithDialogStackContext, WithUser } from '@paintscout/ui'
import {
  Button,
  FormSection,
  InputField,
  withUser,
  KeypadInputField,
  FormSectionTitle,
  Checkbox,
  Switch,
  Select,
  MenuItem,
  withDialogStackContext
} from '@paintscout/ui'
import { Typography, Grid, FormControl, Tooltip, FormControlLabel } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import NumberFormat from 'react-number-format'

import type { QuoteOptions, QuoteDocument, OptionsDocument } from 'paintscout'
import type { QuoteDetails } from '@paintscout/util/builder'
import {
  getDetails,
  setDetails,
  getQuoteOptions,
  setQuoteOptions,
  getCompanyOptions,
  getObjectLabels,
  getFeatures
} from '@paintscout/util/builder'
import get from 'lodash/get'
import AdjustTotalDialog from '../AdjustTotalDialog'

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    displayOptionsSection: {
      paddingTop: theme.spacing(),
      display: 'flex',
      flexDirection: 'column'
    },
    checkboxLabel: {
      ...theme.typography.body1
    },
    discountField: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      width: '100%'
    },
    switch: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      marginBottom: theme.typography.pxToRem(15),
      paddingBottom: `${theme.typography.pxToRem(20)} !important`
    },
    disabledInputField: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P94
    },
    helperText: {
      color: theme.paintscout.palette.brand.Neutral.P70,
      marginTop: theme.spacing() / 2
    },
    adornmentSelect: {
      paddingRight: theme.spacing(2)
    },
    typeSelect: {
      '& $select': {
        borderLeft: 'none'
      }
    },
    select: {},
    visibleToggle: {
      display: 'grid',
      alignItems: 'center',
      gridTemplateColumns: 'auto auto',
      gridGap: theme.spacing()
    }
  })

export interface EditQuoteDetailsFormProps extends WithStyles<typeof styles>, WithUser, WithDialogStackContext {
  quote: QuoteDocument
  options: OptionsDocument
  onChange: (updatedQuote: QuoteDocument) => void
  onComplete?: (event: any) => void
}

/**
 * EditQuoteDetailsForm must have it's own state for the details so that changes made in fields are responsive and
 * the saves to quoteContext can be debounced
 */
export interface EditQuoteDetailsFormState {
  isDirty: boolean
  options: OptionsDocument
  quoteOptions: QuoteOptions
  quote: QuoteDocument
  showBreakdown?: boolean
  showTaxDescription?: boolean
  showDiscountDescription?: boolean
}

/**
 * Displays fields for the Quote Details
 */
class EditQuoteDetailsForm extends React.Component<EditQuoteDetailsFormProps, EditQuoteDetailsFormState> {
  /**
   * Update our details in state when we re-render incase quote was updated from a sync
   */
  public static getDerivedStateFromProps(props: EditQuoteDetailsFormProps, state: EditQuoteDetailsFormState) {
    const { quote } = props

    const quoteOptions = getQuoteOptions({ quote })

    if (!state.isDirty) {
      return {
        quoteOptions
      }
    }

    return null
  }

  constructor(props: EditQuoteDetailsFormProps) {
    super(props)
    const { quote, options } = props

    const quoteOptions = getQuoteOptions({ quote })
    const details = getDetails({ quote, options })

    const showTaxDescription = !!details.tax.description
    const showDiscountDescription = !!details.discount.description

    this.state = {
      options,
      isDirty: false,
      quoteOptions,
      quote,
      showBreakdown: false,
      showTaxDescription,
      showDiscountDescription
    }
  }

  /**
   * Updates the quote in the quote context and is debounced to 1000ms
   */
  public updateQuoteDetails = (details: QuoteDetails) => {
    const { options } = this.props
    const { quote } = this.state
    const updatedQuote = setDetails({ quote, options, details })
    this.setState({ quote: updatedQuote, isDirty: true })
    this.props.onChange(updatedQuote)
  }

  public updateQuoteOptions = (quoteOptions: QuoteOptions) => {
    const { quote } = this.state
    const updatedQuote = setQuoteOptions({ quote, quoteOptions })
    this.setState({ quote: updatedQuote, isDirty: true })
    this.props.onChange(updatedQuote)
  }

  public render() {
    const { classes, options } = this.props
    const { quote, showDiscountDescription, showTaxDescription } = this.state
    const details = getDetails({ quote, options })
    const { clientVisible } = details
    const objectLabels = getObjectLabels({ options })

    const features = getFeatures({ options })
    const materialCostFeature = get(features, 'quotes.materialCost.enabled')
    const salesRateFeature = get(features, 'quotes.salesRate')
    const additionalWorkFeature = get(features, 'quotes.additionalWork')

    const companyOptions = getCompanyOptions({ options })

    const subtotalCalculationText =
      details.totals.price !== details.totals.afterAdjustment ? (
        <NumberFormat
          prefix={`Calculated at: ${objectLabels.currency.symbol}`}
          value={details.totals.price}
          displayType={'text'}
          decimalScale={2}
          fixedDecimalScale={true}
          thousandSeparator={true}
        />
      ) : (
        ' '
      )

    const subTotal =
      details.totals.price === details.totals.afterAdjustment ? details.totals.price : details.totals.afterAdjustment

    const additionalWork = details.totals.additionalWork

    return (
      <div className={classes.root}>
        <FormSection hideDivider={true} style={{ paddingTop: 0 }}>
          {materialCostFeature && (
            <>
              <Grid container={true} spacing={1} direction={'row'}>
                <Grid item={true} xs={12} sm={12}>
                  <InputField
                    format={'price'}
                    classes={{ inputRoot: classes.disabledInputField }}
                    value={details.totals.labour}
                    readOnly={true}
                    label={`Labor (${objectLabels.currency.symbol})`}
                    fullWidth={true}
                    helperText={' '}
                  />
                </Grid>
              </Grid>
              <Grid container={true} spacing={1} direction={'row'}>
                <Grid item={true} xs={12} sm={12}>
                  <InputField
                    format={'price'}
                    classes={{ inputRoot: classes.disabledInputField }}
                    value={details.totals.materials}
                    readOnly={true}
                    label={`Material Cost (${objectLabels.currency.symbol})`}
                    fullWidth={true}
                    helperText={' '}
                  />
                </Grid>
              </Grid>
            </>
          )}
          <Grid container={true} spacing={1} direction={'row'} style={{ alignItems: 'center' }}>
            <Grid item={true} xs={12} sm={12}>
              <InputField
                label={`Sub Total (${objectLabels.currency.symbol})`}
                name={'price'}
                classes={{ inputRoot: classes.disabledInputField }}
                readOnly={true}
                value={subTotal}
                fullWidth={true}
                format={'price'}
                helperText={subtotalCalculationText}
              />
            </Grid>

            {companyOptions.subTotal.allowCustom && (
              <Grid item={true} xs={12} sm={12}>
                <Button icon={EditIcon} variant={'text'} onClick={this.handleEditTotal}>
                  Edit Sub Total
                </Button>
              </Grid>
            )}
          </Grid>

          {additionalWorkFeature && (
            <Grid container={true} spacing={1} direction={'row'} style={{ alignItems: 'center' }}>
              <Grid item={true} xs={12} sm={12}>
                <InputField
                  label={`${objectLabels.additionalWork.value} (${objectLabels.currency.symbol})`}
                  name={'price'}
                  classes={{ inputRoot: classes.disabledInputField }}
                  readOnly={true}
                  value={additionalWork}
                  fullWidth={true}
                  format={'price'}
                />
              </Grid>
            </Grid>
          )}

          <Grid container={true} spacing={1} direction={'row'} alignItems={'center'}>
            <Grid item={true} xs={12} sm={12}>
              <div className={classes.discountField}>
                <KeypadInputField
                  value={details.discount.rate !== undefined ? details.discount.rate : 0}
                  fullWidth={true}
                  margin="dense"
                  format={'price'}
                  label={`Discount (${details.discount.type === 'percentage' ? '%' : objectLabels.currency.symbol})`}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    this.updateDiscount('rate', event.target.value)
                  }}
                  style={{ width: '100%' }}
                  helperText={details.discount.isShown || details.discount.rate === 0 ? ' ' : `Included in Sub Total`}
                  endAdornment={
                    <Select
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                      }}
                      value={details.discount.type === 'percentage' ? 'percentage' : 'value'}
                      classes={{ select: classes.adornmentSelect }}
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                        this.updateDiscount('type', event.target.value)
                      }}
                      disabled={!companyOptions.discount.allowCustom}
                      renderValue={(value: string) => {
                        return <span>{value === 'percentage' ? `%` : objectLabels.currency.symbol}</span>
                      }}
                    >
                      <MenuItem value="percentage">%</MenuItem>
                      <MenuItem value="value">{objectLabels.currency.symbol}</MenuItem>
                    </Select>
                  }
                  autoSelect={true}
                  disabled={!companyOptions.discount.allowCustom}
                />
              </div>
            </Grid>
            <Grid item={true} xs={12} sm={12}>
              {showDiscountDescription ? (
                <InputField
                  value={details.discount.description}
                  label={'Discount Description'}
                  name={'discount description'}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    this.updateDiscount('description', event.target.value)
                  }}
                  fullWidth={true}
                  margin="dense"
                  helperText={' '}
                  disabled={!companyOptions.discount.allowCustom}
                />
              ) : (
                <Button onClick={this.showDescription('discount')} variant={'text'} icon={EditIcon}>
                  Add Description
                </Button>
              )}
            </Grid>
          </Grid>
          {details.discount.isShown && details.discount.rate !== 0 && (
            <Grid container={true} spacing={1} direction={'row'}>
              <Grid item={true} xs={12} sm={12}>
                <InputField
                  value={details.totals.afterDiscount}
                  fullWidth={true}
                  label={`After Discount (${objectLabels.currency.symbol})`}
                  name={'after discount'}
                  format={'price'}
                  margin="dense"
                  readOnly={true}
                  classes={{ inputRoot: classes.disabledInputField }}
                  disabled={!companyOptions.discount.allowCustom}
                />
              </Grid>
            </Grid>
          )}

          <Grid container={true} spacing={1} direction={'row'} alignItems={'center'}>
            <Grid item={true} xs={4} sm={12}>
              <KeypadInputField
                value={details.tax.rate}
                fullWidth={true}
                format={'taxRate'}
                label={'Tax Rate (%)'}
                name={'rate'}
                onChange={this.handleTaxChange}
                margin="dense"
                autoSelect={true}
                helperText={' '}
                disabled={!companyOptions.taxes.allowCustom}
              />
            </Grid>
            <Grid item={true} xs={8} sm={12}>
              {showTaxDescription ? (
                <InputField
                  type={'text'}
                  value={details.tax.description}
                  fullWidth={true}
                  label={'Tax Description'}
                  name={'description'}
                  helperText={' '}
                  onChange={this.handleTaxChange}
                  margin="dense"
                  disabled={!companyOptions.taxes.allowCustom}
                />
              ) : (
                <Button onClick={this.showDescription('tax')} variant={'text'} icon={EditIcon}>
                  Add Description
                </Button>
              )}
            </Grid>
          </Grid>
          {details.tax.rate !== 0 && (
            <Grid container={true} spacing={1} direction={'row'}>
              <Grid item={true} xs={12} sm={12}>
                <InputField
                  value={quote.totals.after_tax}
                  fullWidth={true}
                  label={`Total (${objectLabels.currency.symbol})`}
                  name={'total'}
                  margin="dense"
                  readOnly={true}
                  format={'price'}
                  classes={{ inputRoot: classes.disabledInputField }}
                />
              </Grid>
            </Grid>
          )}
        </FormSection>
        <FormSection>
          <Grid container={true} spacing={3} style={{ alignItems: 'center' }}>
            <Grid item={true} xs={12} sm={12} md={12}>
              <KeypadInputField
                value={details.hourlyRate}
                margin="dense"
                name={'hourlyRate'}
                label={`Hourly Rate (${objectLabels.currency.symbol}/hr)`}
                onChange={this.handleHourlyRateChange}
                fullWidth={true}
                autoSelect={true}
                format={'price'}
                helperText={' '}
                disabled={!companyOptions.hourlyRate.allowCustom}
              />
            </Grid>
            {salesRateFeature && (
              <Grid item={true} xs={12} sm={12} md={12}>
                <InputField
                  format={'price'}
                  classes={{ inputRoot: classes.disabledInputField }}
                  value={details.totals.salesRate}
                  readOnly={true}
                  label={`Sales Rate (${objectLabels.currency.symbol}/hr)`}
                  fullWidth={true}
                  helperText={' '}
                />
              </Grid>
            )}
          </Grid>
          <Grid container={true} spacing={3} style={{ alignItems: 'center' }}>
            <Grid item={true} xs={12} sm={12}>
              <FormControl>
                <Tooltip
                  title={`By un-checking this checkbox, ${objectLabels.quote.plural.toLowerCase()} sent to clients will no longer be accessible.`}
                >
                  <Typography classes={{ root: classes.visibleToggle }} variant={'overline'}>
                    Visible to Client <Switch checked={clientVisible} onChange={this.handleCheckboxChange} />
                  </Typography>
                </Tooltip>
              </FormControl>
            </Grid>
          </Grid>
        </FormSection>
        {this.renderClientNote()}
        {this.renderQuoteOptions()}
      </div>
    )
  }

  public renderClientNote() {
    const { options } = this.props
    const { quote } = this.state
    const features = getFeatures({ options })
    const clientNoteFeature = get(features, 'quotes.clientNote')
    const details = getDetails({ quote, options })

    if (!clientNoteFeature) {
      return null
    }

    return (
      <div>
        <FormSection>
          <FormSectionTitle title={'Client Note'} variant={'h4'} />
          <InputField
            value={details.notes.client}
            fullWidth={true}
            multiline={true}
            onChange={this.handleClientNoteChange}
          />
        </FormSection>
      </div>
    )
  }

  public renderQuoteOptions() {
    const { classes, options } = this.props
    const objectLabels = getObjectLabels({ options })
    const hasOptions = get(options, 'options.features.quotes.advancedOptions.enabled', false)

    if (!hasOptions) {
      return null
    }

    const { quoteOptions } = this.state

    const fields = [
      ['showQuantities', 'Show Quantity Value for Quantity-Based Rates'],
      [
        'groupHours',
        `Show Sub-Total Groups for ${objectLabels.quote.plural.toLowerCase()} with Multiple ${
          objectLabels.quote.value
        } Types`
      ],
      ['separateThousands', 'Separate Thousands Values with commas'],
      ['showPrices', 'Show prices for each item in value column'],
      [
        'showDetailedBreakdown',
        `Show each area ${objectLabels.substrate.value.toLowerCase()} as an additional row with price`
      ],
      ['hideWorkOrderVolumes', 'Hide Product Volumes on Work Order'],
      ['hideWorkOrderMaterialCost', 'Hide Material Costs on Work Order']
    ]

    return (
      <div>
        <FormSection classes={{ root: classes.displayOptionsSection }}>
          <FormSectionTitle
            title={'Display Options'}
            variant={'h4'}
            subTitle={`How ${objectLabels.quote.plural} look to you, your crew, and customers.`}
          />
          {fields.map((field, _index) => {
            const control = (
              <Checkbox checked={quoteOptions[field[0]]} onChange={this.handleOptionChange} name={field[0]} />
            )
            return (
              <FormControlLabel
                key={field[0]}
                control={control}
                label={field[1]}
                classes={{ label: classes.checkboxLabel }}
              />
            )
          })}
        </FormSection>
      </div>
    )
  }

  public showDescription = (type: string) => (_ev: React.MouseEvent<HTMLElement>) => {
    if (type === 'discount') {
      this.setState({
        showDiscountDescription: true
      })
    } else if (type === 'tax') {
      this.setState({
        showTaxDescription: true
      })
    }
  }

  public handleClientNoteChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = ev.target
    const details = getDetails({ quote: this.state.quote, options: this.state.options })
    const updatedDetails = {
      ...details,
      notes: {
        ...details.notes,
        client: value
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  /**
   * Toggle isShown from checkbox
   */
  public handleDiscountShownChange = (ev: any, value: boolean) => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })
    const updatedDetails = {
      ...details,
      discount: {
        ...details.discount,
        isShown: value
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  public handleAdjustmentShownChange = (ev: any, value: boolean) => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })
    const updatedDetails = {
      ...details,
      totalAdjustment: {
        ...details.totalAdjustment,
        isShown: value
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  public handleEditTotal = (_ev: React.MouseEvent<HTMLElement>) => {
    const { options, dialogStackContext } = this.props
    const { quote } = this.state

    this.props.dialogStackContext.openDialog(AdjustTotalDialog, {
      quote,
      options,
      onConfirm: (updatedQuote: QuoteDocument) => {
        this.setState({ quote: updatedQuote })
        this.props.onChange(updatedQuote)
        dialogStackContext.dismissDialog()
      },
      onCancel: () => {
        dialogStackContext.dismissDialog()
      }
    })
  }

  public handleCheckboxChange = (_ev: React.ChangeEvent<HTMLElement>, _value: boolean) => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })

    const updatedDetails = {
      ...details,
      clientVisible: !details.clientVisible
    }

    this.updateQuoteDetails(updatedDetails)
  }

  /**
   * Update discount properties from input elements
   */
  public updateDiscount = (key: keyof QuoteDetails['discount'], value: number | string) => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })

    const updatedDetails = {
      ...details,
      discount: {
        ...details.discount,
        [key]: value
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  public updateAdjustment = (key: keyof QuoteDetails['totalAdjustment']) => (ev: React.ChangeEvent<any>) => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })

    const updatedDetails = {
      ...details,
      totalAdjustment: {
        ...details.totalAdjustment,
        [key]: ev.target.value
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  public handleSubTotalsChange = (event: any) => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })
    const { value } = event.target

    const updatedDetails = {
      ...details,
      totals: {
        ...details.totals,
        price: value
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  public handleTaxChange = (event: any) => {
    const { name, value } = event.target

    const details = getDetails({ quote: this.state.quote, options: this.state.options })
    const updatedDetails = {
      ...details,
      tax: {
        ...details.tax,
        [name]: value
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  public handleHourlyRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const details = getDetails({ quote: this.state.quote, options: this.state.options })

    const hourlyRate = parseFloat(value)

    this.updateQuoteDetails({
      ...details,
      hourlyRate
    })
  }

  public handleOptionChange = (ev: any, value: boolean) => {
    const quoteOptions = {
      ...this.state.quoteOptions,
      [ev.currentTarget.name]: value
    }

    this.setState({
      isDirty: true,
      quoteOptions
    })

    this.updateQuoteOptions(quoteOptions)
  }

  public handleKeyPress = (event: any) => {
    if (this.props.onComplete && event.key === 'Enter') {
      this.props.onComplete(event)
    }
  }

  public toggleBreakDown = (_ev: React.MouseEvent<HTMLElement>) => {
    this.setState({
      showBreakdown: !this.state.showBreakdown
    })
  }
}

export default withStyles(styles)(withDialogStackContext(withUser(EditQuoteDetailsForm)))
